import React, { useEffect, useState } from "react";
import { Box, Button, TextField } from "../design-system";
import { colors } from "../design-system/colors";
import Divider from "../design-system/Divider";
import { AUTO_TAB } from "./constants/accountText";
import { PostWhen, Privacy, useAutoDestination } from "./hooks/useAutoDestination";

/**
 * Account Settings → Auto.
 *
 * One webhook URL. Paste it, hit Send test, and build the make.com scenario against a
 * real packet — no export needed. After that every finished video export is pushed
 * there on its own.
 */
const AutoTab = () => {
  const {
    destination,
    loading,
    saving,
    testing,
    error,
    notice,
    save,
    disconnect,
    sendTest,
    clearMessages,
  } = useAutoDestination();

  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [postWhen, setPostWhen] = useState<PostWhen>("now");
  const [privacy, setPrivacy] = useState<Privacy>("private");

  // Seed the form once the saved destination arrives.
  useEffect(() => {
    setUrl(destination?.url ?? "");
    setEnabled(destination?.enabled ?? true);
    setPostWhen(destination?.post_when ?? "now");
    setPrivacy(destination?.privacy ?? "private");
  }, [destination]);

  const savedUrl = destination?.url ?? "";
  const isDirty =
    url.trim() !== savedUrl ||
    enabled !== (destination?.enabled ?? true) ||
    postWhen !== (destination?.post_when ?? "now") ||
    privacy !== (destination?.privacy ?? "private");
  const canTest = Boolean(savedUrl) && !isDirty;

  return (
    <Box
      sx={{
        backgroundColor: colors.card.profileBackground,
        borderRadius: "16px",
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: "100%",
        minHeight: "400px",
        // The settings window scrolls a flex column: without this the panel is squeezed to
        // the window's height and the settings below spill past its background.
        flexShrink: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontSize: "24px",
          fontWeight: 600,
          lineHeight: "34px",
          color: colors.neutral.white,
        }}
      >
        {AUTO_TAB.SECTION_TITLE}
      </Box>

      <Divider />

      <Box
        component="p"
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontSize: "15px",
          lineHeight: "22px",
          color: colors.neutral[400],
          margin: 0,
        }}
      >
        {AUTO_TAB.DESCRIPTION}
      </Box>

      <TextField
        label={AUTO_TAB.INPUT_LABEL}
        placeholder={AUTO_TAB.INPUT_PLACEHOLDER}
        value={url}
        disabled={loading || saving}
        onChange={(event) => {
          setUrl(event.target.value);
          clearMessages();
        }}
        fullWidth
      />

      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontFamily: "Outfit, sans-serif",
          fontSize: "15px",
          color: colors.neutral[400],
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={enabled}
          disabled={loading || saving}
          onChange={(event) => {
            setEnabled(event.target.checked);
            clearMessages();
          }}
          style={{ width: "16px", height: "16px", accentColor: colors.primary[500] }}
        />
        {AUTO_TAB.ENABLED_LABEL}
      </Box>

      {/* Side by side on a wide screen, so Save stays in view; stacked on a phone. */}
      <Box sx={{ display: "flex", gap: "20px 40px", flexWrap: "wrap" }}>
      <ChoiceRow
        label={AUTO_TAB.WHEN_LABEL}
        value={postWhen}
        disabled={loading || saving}
        options={[
          { key: "now", label: AUTO_TAB.WHEN_NOW },
          { key: "review", label: AUTO_TAB.WHEN_REVIEW },
        ]}
        onChange={(key) => {
          setPostWhen(key as PostWhen);
          clearMessages();
        }}
        hint={postWhen === "review" ? AUTO_TAB.WHEN_REVIEW_HINT : ""}
      />

      <ChoiceRow
        label={AUTO_TAB.PRIVACY_LABEL}
        value={privacy}
        disabled={loading || saving}
        options={[
          { key: "private", label: AUTO_TAB.PRIVACY_PRIVATE },
          { key: "public", label: AUTO_TAB.PRIVACY_PUBLIC },
        ]}
        onChange={(key) => {
          setPrivacy(key as Privacy);
          clearMessages();
        }}
        hint={privacy === "public" ? AUTO_TAB.PUBLIC_HINT : ""}
      />
      </Box>

      <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <Button
          variant="primary"
          loading={saving}
          disabled={loading || saving || !url.trim() || !isDirty}
          onClick={() => save(url.trim(), enabled, postWhen, privacy)}
        >
          {saving ? AUTO_TAB.SAVING : AUTO_TAB.SAVE}
        </Button>

        <Button
          variant="outlined"
          loading={testing}
          disabled={loading || testing || !canTest}
          onClick={sendTest}
        >
          {testing ? AUTO_TAB.TESTING : AUTO_TAB.TEST}
        </Button>

        {savedUrl ? (
          <Button variant="text" disabled={saving} onClick={disconnect}>
            {AUTO_TAB.DISCONNECT}
          </Button>
        ) : null}
      </Box>

      {/* Why Send test is greyed out: it fires at the SAVED url, not what's typed. */}
      {savedUrl && isDirty ? (
        <StatusLine tone="muted" text={AUTO_TAB.TEST_NEEDS_SAVE} />
      ) : null}
      {error ? <StatusLine tone="error" text={error} /> : null}
      {notice ? <StatusLine tone="ok" text={notice} /> : null}

      {destination ? <LastDelivery destination={destination} /> : null}

      <Divider />

      <StatusLine tone="muted" text={AUTO_TAB.DRAFT_NOTE} />
    </Box>
  );
};

/** A label and two-or-more pill choices (one picked), with an optional line under them. */
const ChoiceRow = ({
  label,
  value,
  options,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  value: string;
  options: { key: string; label: string }[];
  onChange: (key: string) => void;
  disabled?: boolean;
  hint?: string;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: "1 1 280px", minWidth: 0 }}>
    <Box
      component="span"
      sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", fontWeight: 600, color: colors.neutral.white }}
    >
      {label}
    </Box>
    <Box role="group" aria-label={label} sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {options.map((option) => {
        const on = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(option.key)}
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              padding: "8px 14px",
              borderRadius: "999px",
              cursor: disabled ? "default" : "pointer",
              border: `1px solid ${on ? colors.primary[500] : colors.neutral[600]}`,
              background: on ? "rgba(236,43,87,0.16)" : "transparent",
              color: on ? colors.neutral.white : colors.neutral[400],
            }}
          >
            {option.label}
          </button>
        );
      })}
    </Box>
    {hint ? <StatusLine tone="muted" text={hint} /> : null}
  </Box>
);

const StatusLine = ({
  tone,
  text,
}: {
  tone: "ok" | "error" | "muted";
  text: string;
}) => (
  <Box
    component="p"
    sx={{
      fontFamily: "Outfit, sans-serif",
      fontSize: "14px",
      lineHeight: "20px",
      margin: 0,
      color:
        tone === "error"
          ? colors.primary[500]
          : tone === "ok"
            ? colors.neutral.white
            : colors.neutral[600],
    }}
  >
    {text}
  </Box>
);

const LastDelivery = ({
  destination,
}: {
  destination: { last_fired_at: string | null; last_status: number | null; last_error: string };
}) => {
  if (!destination.last_fired_at) {
    return <StatusLine tone="muted" text={AUTO_TAB.NEVER_FIRED} />;
  }
  const when = new Date(destination.last_fired_at).toLocaleString();
  if (destination.last_error) {
    return (
      <StatusLine
        tone="error"
        text={`${AUTO_TAB.LAST_FAILED} · ${when} · ${destination.last_error}`}
      />
    );
  }
  return (
    <StatusLine
      tone="muted"
      text={`${AUTO_TAB.LAST_OK} · ${when} · ${destination.last_status ?? ""}`}
    />
  );
};

export default AutoTab;
